import React, { useEffect, useState, useRef } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { supabaseClient } from '../../services/supabase';
import { fetchGHGInventoryData } from './GHGInventorySupabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { compareSectorLevelData, generateSectorComparisonCSV } from '../../services/sectorComparisonService';
import { downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

interface ComparisonData {
  location: string;
  years: number[];
  precalculated: number[];
  liveCalculated: number[];
  differences: number[];
  percentDifferences: number[];
}

interface PrecalculatedVsLiveComparisonChartProps {
  location?: string;
  onDownloadHandlersReady?: (handlers: {
    downloadTotalEmissionsCSV: () => void;
    downloadSectorComparisonCSV: () => void;
    hasSectorComparison: boolean;
  }) => void;
}

export const PrecalculatedVsLiveComparisonChart: React.FC<PrecalculatedVsLiveComparisonChartProps> = ({ location, onDownloadHandlersReady }) => {
  const [comparisonData, setComparisonData] = useState<ComparisonData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sectorComparisonResult, setSectorComparisonResult] = useState<any>(null);
  const chartRef = useRef<ChartJS<'line'>>(null);

  // Handle CSV download for total emissions
  const handleDownloadTotalEmissionsCSV = React.useCallback(() => {
    if (comparisonData.length === 0 || !location) return;
    
    const csvData: any[] = [];
    comparisonData.forEach((data) => {
      data.years.forEach((year, idx) => {
        csvData.push({
          Location: data.location,
          Year: year,
          'Pre-calculated (MT CO2e)': data.precalculated[idx].toFixed(2),
          'Live-calculated (MT CO2e)': data.liveCalculated[idx].toFixed(2),
          'Difference (MT CO2e)': data.differences[idx].toFixed(2),
          'Percent Difference (%)': data.percentDifferences[idx].toFixed(2)
        });
      });
    });
    
    if (csvData.length > 0) {
      const filename = formatFilename(location, 'Precalculated_vs_Live_Total_Emissions_Comparison');
      downloadDataAsCSV(csvData, filename);
    }
  }, [comparisonData, location]);

  // Handle CSV download for sector-level comparison
  const handleDownloadSectorComparisonCSV = React.useCallback(() => {
    if (sectorComparisonResult && location) {
      const csvData = generateSectorComparisonCSV(sectorComparisonResult);
      if (csvData.length > 0) {
        const filename = formatFilename(location, 'Precalculated_vs_Live_Sector_Comparison');
        downloadDataAsCSV(csvData, filename);
      }
    }
  }, [sectorComparisonResult, location]);

  // Expose handlers to parent when they're ready
  React.useEffect(() => {
    if (onDownloadHandlersReady && comparisonData.length > 0) {
      onDownloadHandlersReady({
        downloadTotalEmissionsCSV: handleDownloadTotalEmissionsCSV,
        downloadSectorComparisonCSV: handleDownloadSectorComparisonCSV,
        hasSectorComparison: !!sectorComparisonResult
      });
    }
  }, [onDownloadHandlersReady, comparisonData, sectorComparisonResult, handleDownloadTotalEmissionsCSV, handleDownloadSectorComparisonCSV]);

  useEffect(() => {
    const loadComparison = async () => {
      if (!location) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Initialize location service to get location info
        await supabaseLocationService.initialize();
        
        // Get geofips for the location
        const geofips = await supabaseLocationService.getGeofips(location);
        if (!geofips) {
          throw new Error(`Could not find geofips for location: ${location}`);
        }
        
        // Get location info to determine geography type
        const { data: locationData, error: locationError } = await supabaseClient
          .from('population')
          .select('location_name, geography_type, county')
          .eq('geofips', geofips)
          .limit(1);
        
        if (locationError || !locationData || locationData.length === 0) {
          throw new Error(`Could not find location data for: ${location}`);
        }
        
        
        // Only show the selected location (not the associated county for cities)
        const locations = [location];
        const results: ComparisonData[] = [];

        for (const locationItem of locations) {
          // Fetch pre-calculated data using geofips (more reliable than location_name)
          const { data: precalcData, error: precalcError } = await supabaseClient
            .from('ghg_inventory_precalculated')
            .select('year, total_emissions')
            .eq('geofips', geofips)
            .gte('year', 2005)
            .lte('year', 2023)
            .order('year', { ascending: true });

          if (precalcError) {
            console.warn(`Error fetching pre-calculated data for ${locationItem}:`, precalcError);
            continue;
          }

          // Fetch live-calculated data
          let liveData: Record<number, number> = {};
          try {
            const liveResult = await fetchGHGInventoryData(locationItem);
            // Sum all datasets for each year to get total emissions
            const liveTotalsByYear: Record<number, number> = {};
            
            if (liveResult.chartData && liveResult.chartData.datasets) {
              const years = liveResult.chartData.labels as number[];
              liveResult.chartData.datasets.forEach((dataset: any) => {
                dataset.data.forEach((value: number, index: number) => {
                  const year = years[index];
                  if (year && value !== null && value !== undefined) {
                    liveTotalsByYear[year] = (liveTotalsByYear[year] || 0) + value;
                  }
                });
              });
            }

            liveData = liveTotalsByYear;
          } catch (liveError) {
            console.warn(`Error fetching live-calculated data for ${locationItem}:`, liveError);
            continue;
          }

          // Build comparison data
          const precalcByYear: Record<number, number> = {};
          (precalcData || []).forEach((row: any) => {
            precalcByYear[row.year] = Number(row.total_emissions || 0);
          });

          // Get all years (2005-2023)
          const allYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
          const years: number[] = [];
          const precalculated: number[] = [];
          const liveCalculated: number[] = [];
          const differences: number[] = [];
          const percentDifferences: number[] = [];

          allYears.forEach(year => {
            const precalc = precalcByYear[year] || 0;
            const live = liveData[year] || 0;

            if (precalc > 0 || live > 0) {
              years.push(year);
              precalculated.push(precalc);
              liveCalculated.push(live);
              
              const diff = live - precalc;
              differences.push(diff);
              
              const percentDiff = precalc > 0 ? (diff / precalc) * 100 : (live > 0 ? 100 : 0);
              percentDifferences.push(percentDiff);
            }
          });

          // Console log detailed comparison
          console.log(`\n=== ${locationItem} Total Emissions Comparison ===`);
          console.log('Year | Pre-calculated | Live-calculated | Difference | % Difference');
          console.log('-----|----------------|-----------------|------------|--------------');
          years.forEach((year, idx) => {
            console.log(
              `${year} | ${precalculated[idx].toLocaleString().padStart(14)} | ${liveCalculated[idx].toLocaleString().padStart(15)} | ${differences[idx].toLocaleString().padStart(10)} | ${percentDifferences[idx].toFixed(2)}%`
            );
          });

          results.push({
            location: locationItem,
            years,
            precalculated,
            liveCalculated,
            differences,
            percentDifferences
          });
        }

        setComparisonData(results);

        // Also run sector-level comparison for detailed debugging
        console.log('\n🔍 Running sector-level comparison...');
        try {
          const sectorResult = await compareSectorLevelData(location);
          setSectorComparisonResult(sectorResult);
        } catch (sectorError) {
          console.warn('⚠️ Sector-level comparison failed (non-fatal):', sectorError);
        }
      } catch (err) {
        console.error('Error loading comparison data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load comparison data');
      } finally {
        setLoading(false);
      }
    };

    loadComparison();
  }, [location]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '500px',
        fontSize: '16px',
        color: '#6b7280'
      }}>
        Loading comparison data...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '500px',
        fontSize: '16px',
        color: '#ef4444'
      }}>
        Error: {error}
      </div>
    );
  }


  if (comparisonData.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '500px',
        fontSize: '16px',
        color: '#6b7280'
      }}>
        No comparison data available
      </div>
    );
  }

  // Create datasets - one line per location, showing both pre-calculated and live-calculated
  const datasets: any[] = [];
  
  comparisonData.forEach((data, locIndex) => {
    // Pre-calculated line
    datasets.push({
      label: `${data.location} (Pre-calculated)`,
      data: data.precalculated,
      borderColor: locIndex === 0 ? '#3b82f6' : '#8b5cf6',
      backgroundColor: locIndex === 0 ? 'rgba(59, 130, 246, 0.1)' : 'rgba(139, 92, 246, 0.1)',
      borderWidth: 2,
      borderDash: [5, 5],
      fill: false,
      tension: 0.2,
      pointRadius: 3,
      pointHoverRadius: 5
    });

    // Live-calculated line
    datasets.push({
      label: `${data.location} (Live-calculated)`,
      data: data.liveCalculated,
      borderColor: locIndex === 0 ? '#10b981' : '#22c55e',
      backgroundColor: locIndex === 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(34, 197, 94, 0.1)',
      borderWidth: 2,
      fill: false,
      tension: 0.2,
      pointRadius: 3,
      pointHoverRadius: 5
    });
  });

  // Use first location's years (they should all be the same)
  const years = comparisonData[0]?.years || [];

  const chartData = {
    labels: years,
    datasets
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          padding: 15
        }
      },
      title: {
        display: true,
        text: location ? `Pre-calculated vs Live-calculated GHG Inventory Comparison - ${location}` : 'Pre-calculated vs Live-calculated GHG Inventory Comparison',
        font: {
          size: 16,
          weight: 'bold' as const
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            if (value === null) return undefined;
            
            // Find the location data to show difference
            const locationName = label.split(' (')[0];
            const locationData = comparisonData.find(d => d.location === locationName);
            
            if (locationData) {
              const yearIndex = locationData.years.indexOf(context.parsed.x);
              if (yearIndex >= 0) {
                const diff = locationData.differences[yearIndex];
                const percentDiff = locationData.percentDifferences[yearIndex];
                const isPrecalc = label.includes('Pre-calculated');
                const otherValue = isPrecalc 
                  ? locationData.liveCalculated[yearIndex]
                  : locationData.precalculated[yearIndex];
                
                return [
                  `${label}: ${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} MT CO₂e`,
                  isPrecalc 
                    ? `Live: ${otherValue.toLocaleString(undefined, { maximumFractionDigits: 0 })} MT CO₂e`
                    : `Pre-calc: ${otherValue.toLocaleString(undefined, { maximumFractionDigits: 0 })} MT CO₂e`,
                  `Difference: ${diff >= 0 ? '+' : ''}${diff.toLocaleString(undefined, { maximumFractionDigits: 0 })} MT CO₂e (${percentDiff >= 0 ? '+' : ''}${percentDiff.toFixed(2)}%)`
                ];
              }
            }
            
            return `${label}: ${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} MT CO₂e`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Year',
          font: {
            size: 12
          }
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Total GHG Emissions (MT CO₂e)',
          font: {
            size: 12
          }
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          callback: function(value: any) {
            return Number(value).toLocaleString();
          }
        }
      }
    },
    interaction: {
      mode: 'index' as const,
      intersect: false
    }
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '500px',
      position: 'relative'
    }}>
      <Line
        ref={chartRef}
        data={chartData}
        options={chartOptions}
      />

      {/* Summary table */}
      <div style={{ marginTop: '20px', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
              <th style={{ padding: '8px', textAlign: 'left', borderRight: '1px solid #d1d5db' }}>Location</th>
              <th style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #d1d5db' }}>Year</th>
              <th style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #d1d5db' }}>Pre-calculated</th>
              <th style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #d1d5db' }}>Live-calculated</th>
              <th style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #d1d5db' }}>Difference</th>
              <th style={{ padding: '8px', textAlign: 'right' }}>% Difference</th>
            </tr>
          </thead>
          <tbody>
            {comparisonData.flatMap((data, locIdx) => {
              // Show last 5 years + a few key years
              const keyYears = [2005, 2010, 2015, 2020, 2021, 2022, 2023];
              const yearsToShow = Array.from(new Set([
                ...data.years.filter(y => keyYears.includes(y)),
                ...data.years.slice(-5) // Last 5 years
              ])).sort((a, b) => a - b);

              return yearsToShow.map((year, yearIdx) => {
                const yearIndex = data.years.indexOf(year);
                if (yearIndex === -1) return null;

                const precalc = data.precalculated[yearIndex];
                const live = data.liveCalculated[yearIndex];
                const diff = data.differences[yearIndex];
                const percentDiff = data.percentDifferences[yearIndex];

                return (
                  <tr 
                    key={`${locIdx}-${year}`}
                    style={{ 
                      borderBottom: '1px solid #e5e7eb',
                      backgroundColor: yearIdx % 2 === 0 ? '#ffffff' : '#f9fafb'
                    }}
                  >
                    <td style={{ padding: '8px', borderRight: '1px solid #d1d5db', fontWeight: yearIdx === 0 ? 'bold' : 'normal' }}>
                      {yearIdx === 0 ? data.location : ''}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #d1d5db' }}>{year}</td>
                    <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #d1d5db' }}>
                      {precalc.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #d1d5db' }}>
                      {live.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td style={{ 
                      padding: '8px', 
                      textAlign: 'right', 
                      borderRight: '1px solid #d1d5db',
                      color: Math.abs(diff) > 1000 ? '#ef4444' : Math.abs(diff) > 100 ? '#f59e0b' : '#10b981'
                    }}>
                      {diff >= 0 ? '+' : ''}{diff.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td style={{ 
                      padding: '8px', 
                      textAlign: 'right',
                      color: Math.abs(percentDiff) > 5 ? '#ef4444' : Math.abs(percentDiff) > 1 ? '#f59e0b' : '#10b981'
                    }}>
                      {percentDiff >= 0 ? '+' : ''}{percentDiff.toFixed(2)}%
                    </td>
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

